import { type FormEvent, useEffect, useMemo, useState } from "react";
import { io } from "socket.io-client";
import { Badge, Button, Logo, orderTone } from "@globaldine/ui";
import {
  localize,
  supportedLocales,
  type ItemStatus,
  type Locale,
  type MerchantUser,
  type Order,
  type OrderItem,
  type Store,
} from "@globaldine/contracts";
import {
  errorLabel,
  localeLabels,
  statusLabel,
  uiLabel,
  valueLabel,
} from "@globaldine/i18n";
import {
  Bell,
  BellOff,
  Check,
  ChefHat,
  Clock3,
  Flame,
  LogOut,
  Monitor,
  Printer,
  RefreshCw,
  Snowflake,
  Soup,
  Wifi,
} from "lucide-react";

const API_URL = import.meta.env.VITE_API_URL ?? "http://localhost:4000";
const TOKEN_KEY = "globaldine.kitchen.token";
let sessionToken = localStorage.getItem(TOKEN_KEY) ?? "";
async function api<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(`${API_URL}${path}`, {
    ...init,
    headers: {
      "Content-Type": "application/json",
      ...(sessionToken ? { Authorization: `Bearer ${sessionToken}` } : {}),
    },
  });
  const body = await response.json();
  if (!response.ok) throw new Error(body.error ?? "REQUEST_FAILED");
  return body;
}

type Column = { id: "new" | "preparing" | "ready"; statuses: ItemStatus[] };
const columns: Column[] = [
  { id: "new", statuses: ["queued"] },
  { id: "preparing", statuses: ["preparing"] },
  { id: "ready", statuses: ["ready"] },
];

const kitchenMessages: Record<Locale, Record<string, string>> = {
  vi: {
    loginTitle: "Đăng nhập bếp",
    loginHint: "Dùng tài khoản nhân viên của cửa hàng",
    merchantCode: "Mã cửa hàng",
    username: "Tên đăng nhập",
    password: "Mật khẩu",
    signIn: "Đăng nhập",
    loading: "Đang tải",
    kitchen: "Bếp GlobeDine",
    kds: "Màn hình bếp",
    callScreen: "Màn hình gọi món",
    start: "Bắt đầu",
    complete: "Hoàn thành",
    serve: "Phục vụ",
    reprint: "In lại",
    newOrder: "Đơn mới",
    primary: "Ngôn ngữ chính",
    secondary: "Ngôn ngữ phụ",
  },
  "zh-Hans": {
    loginTitle: "后厨登录",
    loginHint: "使用本门店员工账号登录",
    merchantCode: "商户编号",
    username: "用户名",
    password: "密码",
    signIn: "登录",
    loading: "加载中",
    kitchen: "GlobeDine 后厨",
    kds: "后厨看板",
    callScreen: "呼叫屏",
    start: "开始制作",
    complete: "完成出品",
    serve: "出餐",
    reprint: "重新打印",
    newOrder: "新订单",
    primary: "主语言",
    secondary: "辅助语言",
  },
  "zh-Hant": {
    loginTitle: "後廚登入",
    loginHint: "使用本門店員工帳號登入",
    merchantCode: "商戶編號",
    username: "使用者名稱",
    password: "密碼",
    signIn: "登入",
    loading: "載入中",
    kitchen: "GlobeDine 後廚",
    kds: "後廚看板",
    callScreen: "呼叫屏",
    start: "開始製作",
    complete: "完成出品",
    serve: "出餐",
    reprint: "重新列印",
    newOrder: "新訂單",
    primary: "主語言",
    secondary: "輔助語言",
  },
  en: {
    loginTitle: "Kitchen sign in",
    loginHint: "Use a staff account for this store",
    merchantCode: "Merchant code",
    username: "Username",
    password: "Password",
    signIn: "Sign in",
    loading: "Loading",
    kitchen: "GlobeDine Kitchen",
    kds: "Kitchen display",
    callScreen: "Call screen",
    start: "Start",
    complete: "Ready",
    serve: "Serve",
    sources: "Sources",
    reprint: "Reprint",
    newOrder: "New order",
    primary: "Primary language",
    secondary: "Secondary language",
  },
  ko: {
    loginTitle: "주방 로그인",
    loginHint: "이 매장의 직원 계정을 사용하세요",
    merchantCode: "가맹점 코드",
    username: "사용자명",
    password: "비밀번호",
    signIn: "로그인",
    loading: "불러오는 중",
    kitchen: "GlobeDine 주방",
    kds: "주방 화면",
    callScreen: "호출 화면",
    start: "조리 시작",
    complete: "완료",
    serve: "서빙 완료",
    reprint: "다시 인쇄",
    newOrder: "새 주문",
    primary: "기본 언어",
    secondary: "보조 언어",
  },
  ru: {
    loginTitle: "Вход для кухни",
    loginHint: "Используйте учётную запись сотрудника заведения",
    merchantCode: "Код заведения",
    username: "Логин",
    password: "Пароль",
    signIn: "Войти",
    loading: "Загрузка",
    kitchen: "Кухня GlobeDine",
    kds: "Экран кухни",
    callScreen: "Экран вызова",
    start: "Начать",
    complete: "Готово",
    serve: "Выдать",
    reprint: "Печать снова",
    newOrder: "Новый заказ",
    primary: "Основной язык",
    secondary: "Дополнительный язык",
  },
  ja: {
    loginTitle: "厨房ログイン",
    loginHint: "この店舗のスタッフアカウントを使用してください",
    merchantCode: "加盟店コード",
    username: "ユーザー名",
    password: "パスワード",
    signIn: "ログイン",
    loading: "読み込み中",
    kitchen: "GlobeDine 厨房",
    kds: "厨房画面",
    callScreen: "呼出画面",
    start: "調理開始",
    complete: "完了",
    serve: "提供済み",
    reprint: "再印刷",
    newOrder: "新規注文",
    primary: "主言語",
    secondary: "補助言語",
  },
  th: {
    loginTitle: "เข้าสู่ระบบครัว",
    loginHint: "ใช้บัญชีพนักงานของร้านนี้",
    merchantCode: "รหัสร้านค้า",
    username: "ชื่อผู้ใช้",
    password: "รหัสผ่าน",
    signIn: "เข้าสู่ระบบ",
    loading: "กำลังโหลด",
    kitchen: "ครัว GlobeDine",
    kds: "หน้าจอครัว",
    callScreen: "หน้าจอเรียก",
    start: "เริ่มทำ",
    complete: "เสร็จแล้ว",
    serve: "เสิร์ฟ",
    reprint: "พิมพ์ใหม่",
    newOrder: "ออเดอร์ใหม่",
    primary: "ภาษาหลัก",
    secondary: "ภาษารอง",
  },
};

export function App() {
  const [token, setToken] = useState(sessionToken);
  const [user, setUser] = useState<MerchantUser>();
  const [credentials, setCredentials] = useState({
    merchantCode: "",
    username: "",
    password: "",
  });
  const [stores, setStores] = useState<Store[]>([]);
  const [storeId, setStoreId] = useState("");
  const [orders, setOrders] = useState<Order[]>([]);
  const [station, setStation] = useState<"all" | "hot" | "cold" | "bar">("all");
  const [primaryLanguage, setPrimaryLanguage] = useState<Locale>("vi");
  const [secondaryLanguage, setSecondaryLanguage] = useState<Locale>("zh-Hans");
  const [sound, setSound] = useState(true);
  const [mode, setMode] = useState<"kds" | "pickup">("kds");
  const [toast, setToast] = useState("");
  const store = stores.find((item) => item.id === storeId);
  const kt = (key: string) =>
    kitchenMessages[primaryLanguage][key] ?? kitchenMessages.en[key] ?? key;

  useEffect(() => {
    if (!token) return;
    api<{ user: MerchantUser; stores: Store[] }>("/api/auth/me")
      .then((result) => {
        if (!result.user.permissions.includes("manage_orders"))
          throw new Error("KITCHEN_ACCESS_DENIED");
        setUser(result.user);
        setStores(result.stores);
        setStoreId((current) => current || result.stores[0]?.id || "");
        setPrimaryLanguage(result.user.preferredLanguage);
      })
      .catch(() => logout());
  }, [token]);

  async function login(event: FormEvent) {
    event.preventDefault();
    try {
      const result = await api<{
        token: string;
        user: MerchantUser;
        stores: Store[];
      }>("/api/auth/login", {
        method: "POST",
        body: JSON.stringify(credentials),
      });
      if (!result.user.permissions.includes("manage_orders"))
        throw new Error("KITCHEN_ACCESS_DENIED");
      sessionToken = result.token;
      localStorage.setItem(TOKEN_KEY, result.token);
      setToken(result.token);
      setUser(result.user);
      setStores(result.stores);
      setStoreId(result.stores[0]?.id ?? "");
    } catch (error) {
      setToast(errorLabel((error as Error).message, primaryLanguage));
    }
  }

  function logout() {
    sessionToken = "";
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
    setUser(undefined);
    setStores([]);
    setStoreId("");
  }

  const refresh = async () => {
    const [currentStore, orderList] = await Promise.all([
      api<Store>(`/api/stores/${storeId}`),
      api<Order[]>(`/api/orders?storeId=${storeId}`),
    ]);
    setStores([currentStore]);
    setOrders(
      orderList.filter(
        (order) =>
          !["awaiting_confirmation", "completed", "cancelled"].includes(
            order.status,
          ),
      ),
    );
  };
  useEffect(() => {
    if (user && storeId)
      refresh().catch((error) =>
        setToast(errorLabel(error.message, primaryLanguage)),
      );
  }, [user?.id, storeId]);
  useEffect(() => {
    if (!token || !storeId) return;
    const socket = io(API_URL, { auth: { token } });
    socket.emit("subscribe", [`store:${storeId}`]);
    const update = (order: Order) => {
      setOrders((current) =>
        [order, ...current.filter((item) => item.id !== order.id)].filter(
          (item) =>
            !["awaiting_confirmation", "completed", "cancelled"].includes(
              item.status,
            ),
        ),
      );
      if (sound && order.status === "confirmed")
        setToast(`${kt("newOrder")} ${order.displayNumber}`);
    };
    socket.on("order:new", update);
    socket.on("order:update", update);
    return () => {
      socket.close();
    };
  }, [primaryLanguage, sound, storeId, token]);
  useEffect(() => {
    if (!toast) return;
    const timer = setTimeout(() => setToast(""), 2200);
    return () => clearTimeout(timer);
  }, [toast]);

  const visibleOrders = useMemo(
    () =>
      orders
        .map((order) => ({
          ...order,
          items: order.items.filter(
            (item) => station === "all" || item.station === station,
          ),
        }))
        .filter((order) => order.items.length > 0),
    [orders, station],
  );
  type KitchenGroup = {
    key: string;
    name: OrderItem["nameSnapshot"];
    entries: Array<{ order: Order; item: OrderItem }>;
    quantity: number;
  };
  const groupsFor = (statuses: ItemStatus[]) => {
    const grouped = new Map<string, KitchenGroup>();
    for (const order of visibleOrders)
      for (const item of order.items.filter((candidate) =>
        statuses.includes(candidate.status),
      )) {
        const key = item.menuItemId;
        const group = grouped.get(key) ?? {
          key,
          name: item.nameSnapshot,
          entries: [],
          quantity: 0,
        };
        group.entries.push({ order, item });
        group.quantity += item.quantity;
        grouped.set(key, group);
      }
    return [...grouped.values()].sort(
      (a, b) =>
        Math.min(
          ...a.entries.map(({ order }) => new Date(order.createdAt).getTime()),
        ) -
        Math.min(
          ...b.entries.map(({ order }) => new Date(order.createdAt).getTime()),
        ),
    );
  };
  const readyOrders = orders.filter((order) =>
    ["ready", "served"].includes(order.status),
  );
  const callScreenLanguages = store?.languages?.length
    ? store.languages
    : [primaryLanguage];
  const callTitle = (status: string) =>
    callScreenLanguages
      .map((locale) => statusLabel(status, locale))
      .join(" · ");

  async function setGroupStatus(group: KitchenGroup, status: ItemStatus) {
    try {
      const affected = [
        ...new Map(
          group.entries.map(({ order }) => [order.id, order]),
        ).values(),
      ];
      if (status === "preparing")
        await Promise.all(
          affected
            .filter((order) => order.status === "confirmed")
            .map((order) =>
              api(`/api/orders/${order.id}/status`, {
                method: "POST",
                body: JSON.stringify({ actor: "kitchen", status: "preparing" }),
              }),
            ),
        );
      await Promise.all(
        group.entries.map(({ order, item }) =>
          api<Order>(`/api/orders/${order.id}/items/${item.orderItemId}`, {
            method: "PATCH",
            body: JSON.stringify({ status }),
          }),
        ),
      );
      const latest = await Promise.all(
        affected.map((order) => api<Order>(`/api/orders/${order.id}`)),
      );
      for (const order of latest) {
        const allProduced = order.items.every((item) =>
          ["ready", "served", "cancelled"].includes(item.status),
        );
        if (allProduced && order.status === "preparing")
          await api(`/api/orders/${order.id}/status`, {
            method: "POST",
            body: JSON.stringify({ actor: "kitchen", status: "ready" }),
          });
        const allServed = order.items.every((item) =>
          ["served", "cancelled"].includes(item.status),
        );
        if (status === "served" && allServed) {
          const current =
            allProduced && order.status === "preparing"
              ? "ready"
              : order.status;
          if (current === "ready")
            await api(`/api/orders/${order.id}/status`, {
              method: "POST",
              body: JSON.stringify({
                actor: "kitchen",
                status:
                  order.serviceMode === "self_pickup" ? "completed" : "served",
              }),
            });
        }
      }
      await refresh();
    } catch (error) {
      setToast(errorLabel((error as Error).message, primaryLanguage));
    }
  }

  function minutesOpen(order: Order) {
    return Math.max(
      0,
      Math.floor((Date.now() - new Date(order.createdAt).getTime()) / 60_000),
    );
  }

  function Ticket({
    group,
    column,
  }: {
    group: KitchenGroup;
    column: Column["id"];
  }) {
    const oldest = group.entries.reduce(
      (result, entry) =>
        new Date(entry.order.createdAt) < new Date(result.createdAt)
          ? entry.order
          : result,
      group.entries[0]!.order,
    );
    const urgent = minutesOpen(oldest) >= 15;
    return (
      <article className={`ticket ${urgent ? "urgent" : ""}`}>
        <header className="ticket-head">
          <div>
            <strong>
              {group.quantity}× {localize(group.name, primaryLanguage)}
            </strong>
            {secondaryLanguage !== primaryLanguage && (
              <span className="tiny muted" style={{ display: "block" }}>
                {localize(group.name, secondaryLanguage)}
              </span>
            )}
          </div>
          <Badge tone={urgent ? "red" : "neutral"}>
            <Clock3 size={11} />
            {minutesOpen(oldest)} min
          </Badge>
        </header>
        <div className="ticket-body">
          {group.entries.map(({ order, item }) => (
            <div className="ticket-item" key={item.orderItemId}>
              <strong>{item.quantity}×</strong>
              <div>
                <h4>
                  {order.tableId ?? order.pickupNumber ?? order.displayNumber}
                </h4>
                <p>
                  {order.displayNumber} ·{" "}
                  {valueLabel(order.fulfillmentMode, primaryLanguage)}
                </p>
                {item.modifiers.map((modifier) => (
                  <p key={modifier.optionId}>
                    • {localize(modifier.name, primaryLanguage)}
                  </p>
                ))}
                {item.notes && (
                  <p style={{ color: "var(--coral)", fontWeight: 700 }}>
                    ⚑ {item.notes}
                  </p>
                )}
              </div>
            </div>
          ))}
        </div>
        <footer className="ticket-footer">
          {column === "new" && (
            <Button
              className="small"
              onClick={() => setGroupStatus(group, "preparing")}
            >
              <Flame size={13} />
              {kt("start")}
            </Button>
          )}
          {column === "preparing" && (
            <Button
              className="small"
              onClick={() => setGroupStatus(group, "ready")}
            >
              <Check size={13} />
              {kt("complete")}
            </Button>
          )}
          {column === "ready" && (
            <Button
              className="small"
              onClick={() => setGroupStatus(group, "served")}
            >
              <Check size={13} />
              {kt("serve")}
            </Button>
          )}
          <button className="icon-button" title={kt("reprint")}>
            <Printer size={15} />
          </button>
        </footer>
      </article>
    );
  }

  if (!token || !user)
    return (
      <div className="auth-page">
        <form className="auth-card stack" onSubmit={login}>
          <Logo />
          <div>
            <div className="split">
              <div>
                <h1>{kt("loginTitle")}</h1>
                <p className="muted">{kt("loginHint")}</p>
              </div>
              <select
                className="select compact"
                value={primaryLanguage}
                onChange={(event) =>
                  setPrimaryLanguage(event.target.value as Locale)
                }
              >
                {supportedLocales.map((locale) => (
                  <option key={locale} value={locale}>
                    {localeLabels[locale]}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <label className="field">
            <span>{kt("merchantCode")}</span>
            <input
              className="input"
              value={credentials.merchantCode}
              onChange={(event) =>
                setCredentials({
                  ...credentials,
                  merchantCode: event.target.value,
                })
              }
              required
            />
          </label>
          <label className="field">
            <span>{kt("username")}</span>
            <input
              className="input"
              value={credentials.username}
              onChange={(event) =>
                setCredentials({ ...credentials, username: event.target.value })
              }
              required
            />
          </label>
          <label className="field">
            <span>{kt("password")}</span>
            <input
              className="input"
              type="password"
              value={credentials.password}
              onChange={(event) =>
                setCredentials({ ...credentials, password: event.target.value })
              }
              required
            />
          </label>
          <Button>{kt("signIn")}</Button>
          {toast && <div className="form-error">{toast}</div>}
        </form>
      </div>
    );

  return (
    <div className="kds">
      <header className="topbar">
        <div className="topbar-title">
          <h1 style={{ display: "flex", alignItems: "center", gap: 9 }}>
            <ChefHat size={23} /> {kt("kitchen")}
          </h1>
          <p>
            {store ? localize(store.name, primaryLanguage) : kt("loading")} ·{" "}
            {valueLabel(station, primaryLanguage)}
          </p>
        </div>
        <div className="topbar-actions">
          <div className="segmented">
            <button
              className={mode === "kds" ? "active" : ""}
              onClick={() => setMode("kds")}
            >
              <ChefHat size={14} /> {kt("kds")}
            </button>
            <button
              className={mode === "pickup" ? "active" : ""}
              onClick={() => setMode("pickup")}
            >
              <Monitor size={14} /> {kt("callScreen")}
            </button>
          </div>
          <select
            className="select"
            value={storeId}
            onChange={(event) => setStoreId(event.target.value)}
          >
            {stores.map((item) => (
              <option key={item.id} value={item.id}>
                {localize(item.name, primaryLanguage)}
              </option>
            ))}
          </select>
          <select
            className="select"
            aria-label={kt("primary")}
            value={primaryLanguage}
            onChange={(event) =>
              setPrimaryLanguage(event.target.value as Locale)
            }
          >
            {supportedLocales.map((locale) => (
              <option key={locale} value={locale}>
                {localeLabels[locale]}
              </option>
            ))}
          </select>
          <select
            className="select"
            aria-label={kt("secondary")}
            value={secondaryLanguage}
            onChange={(event) =>
              setSecondaryLanguage(event.target.value as Locale)
            }
          >
            {supportedLocales.map((locale) => (
              <option key={locale} value={locale}>
                {localeLabels[locale]}
              </option>
            ))}
          </select>
          <button className="icon-button" onClick={() => setSound(!sound)}>
            {sound ? <Bell size={17} /> : <BellOff size={17} />}
          </button>
          <button className="icon-button" onClick={refresh}>
            <RefreshCw size={17} />
          </button>
          <button className="icon-button" onClick={logout}>
            <LogOut size={17} />
          </button>
        </div>
      </header>
      {mode === "kds" ? (
        <>
          <div
            style={{
              padding: "13px 18px 0",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 10,
            }}
          >
            <div className="segmented">
              {(["all", "hot", "cold", "bar"] as const).map((value) => (
                <button
                  key={value}
                  className={station === value ? "active" : ""}
                  onClick={() => setStation(value)}
                >
                  {value === "hot" ? (
                    <Flame size={13} />
                  ) : value === "cold" ? (
                    <Snowflake size={13} />
                  ) : value === "bar" ? (
                    <Soup size={13} />
                  ) : null}
                  {valueLabel(value, primaryLanguage)}
                </button>
              ))}
            </div>
            <Badge tone="green">
              <Wifi size={11} /> {uiLabel("online", primaryLanguage)} ·{" "}
              {visibleOrders.length} {uiLabel("order", primaryLanguage)}
            </Badge>
          </div>
          <main className="kds-board">
            {columns.map((column) => {
              const groups = groupsFor(column.statuses);
              return (
                <section className="kds-column" key={column.id}>
                  <header className="kds-column-head">
                    <h2>
                      {statusLabel(
                        column.id === "new" ? "queued" : column.id,
                        primaryLanguage,
                      )}
                    </h2>
                    <Badge
                      tone={
                        column.id === "new"
                          ? "amber"
                          : column.id === "preparing"
                            ? "blue"
                            : "green"
                      }
                    >
                      {groups.length}
                    </Badge>
                  </header>
                  <div className="kds-cards">
                    {groups.map((group) => (
                      <Ticket
                        key={`${group.key}-${column.id}`}
                        group={group}
                        column={column.id}
                      />
                    ))}
                    {groups.length === 0 && (
                      <div
                        style={{
                          textAlign: "center",
                          color: "#71817d",
                          padding: 35,
                        }}
                      >
                        <Check size={28} />
                        <p className="tiny" style={{ marginTop: 7 }}>
                          {uiLabel("allClear", primaryLanguage)}
                        </p>
                      </div>
                    )}
                  </div>
                </section>
              );
            })}
          </main>
        </>
      ) : (
        <main
          style={{
            minHeight: "calc(100vh - 76px)",
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 2,
            background: "#31413e",
            padding: 2,
          }}
        >
          <section
            style={{
              background: "#172321",
              padding: "5vw",
              textAlign: "center",
            }}
          >
            <p style={{ color: "#9dafaa", letterSpacing: 2 }}>
              {callTitle("preparing")}
            </p>
            <div
              style={{
                display: "flex",
                gap: "3vw",
                justifyContent: "center",
                flexWrap: "wrap",
                marginTop: "5vh",
              }}
            >
              {orders
                .filter(
                  (order) => order.status === "preparing" && order.pickupNumber,
                )
                .map((order) => (
                  <strong
                    key={order.id}
                    style={{ fontSize: "clamp(34px,5vw,70px)" }}
                  >
                    {order.pickupNumber}
                  </strong>
                ))}
            </div>
          </section>
          <section
            style={{
              background: "#0d6b5e",
              padding: "5vw",
              textAlign: "center",
            }}
          >
            <p style={{ color: "#d5eee8", letterSpacing: 2 }}>
              {callTitle("ready")}
            </p>
            <div
              style={{
                display: "flex",
                gap: "3vw",
                justifyContent: "center",
                flexWrap: "wrap",
                marginTop: "5vh",
              }}
            >
              {readyOrders
                .filter((order) => order.pickupNumber)
                .map((order) => (
                  <strong
                    key={order.id}
                    style={{ fontSize: "clamp(46px,7vw,92px)" }}
                  >
                    {order.pickupNumber}
                  </strong>
                ))}
            </div>
          </section>
        </main>
      )}
      {toast && <div className="toast">{toast}</div>}
    </div>
  );
}
